import { init as initApm } from "@elastic/apm-rum";
import { decode } from "react-error-decoder";
import { DOM_MUTATION_ERROR } from "@/utils/dom-mutation-error";
import { environment } from "@/utils/enviroment";
import { firstDiff, normalizeHtml, serializeBody, withoutForeignBodyChildren } from "@/utils/hydration-diff";

const apm = initApm({
	serviceName: "reshopper-marketplace",
	serverUrl: "https://790c09218fca4e8cb3123d9d3879d0d7.apm.eu-west-1.aws.cloud.es.io:443",
	// Set only on release builds (the `build` target's `prod` configuration); must match the service_version
	// upload-sourcemaps.sh sends so APM can de-minify stack traces against this release.
	serviceVersion: process.env.NEXT_PUBLIC_RELEASE_VERSION,
	environment: environment.stackName,
	// Matches the backend's head-based trace sampling rate — reshopper:OtelTracesSamplerArg
	// in src/backend/Reshopper.Infrastructure/Pulumi.prod.yaml (0.02) and Pulumi.dev.yaml
	// (1.0) — so RUM and backend transaction volumes are comparable in APM. Errors are
	// captured independently of this rate.
	transactionSampleRate: environment.name === "prod" ? 0.02 : 1,
});

const HYDRATION_ERROR = /Minified React error #(418|423|425)|Hydration failed/;

// Google Translate marks <html> and rewrites its lang, so a lang other than the page's own means a translated page.
const pageStateTags = () => {
	const html = document.documentElement;
	return {
		route: location.pathname,
		lang: navigator.language,
		google_translate: html.classList.contains("translated-ltr") || html.classList.contains("translated-rtl"),
		html_lang: html.lang,
		grammarly: document.querySelector("grammarly-desktop-integration") !== null,
	};
};

// The filters run when the queue flushes, by when a navigation or "Show original" may have changed the page,
// so the state is taken as each error is thrown. React reports an uncaught commit error through reportError,
// which dispatches this event too.
const domMutationPageStates: ReturnType<typeof pageStateTags>[] = [];
window.addEventListener("error", (event) => {
	if (DOM_MUTATION_ERROR.test(event.message)) {
		domMutationPageStates.push(pageStateTags());
	}
});

// This module runs before React hydrates, so this is the server's HTML. Taken on every page load rather
// than sampled, so every hydration error in APM carries a diff; development is skipped because React
// shows its own diff there.
let serverHtml: string | undefined;
let serverHtmlStillStreaming = false;
if (process.env.NODE_ENV === "production") {
	try {
		serverHtml = serializeBody();
		// Suspense boundaries that have not streamed in yet show up in the diff as divergences of their own.
		serverHtmlStillStreaming = document.readyState === "loading";
	} catch {
		serverHtml = undefined;
	}
}

// Production hydration errors carry no diff. Elastic already captures the error itself (Next.js hands it
// to window.reportError), so the filter enriches that event rather than logging a second one. It runs
// when the queue flushes, well after React has finished client-rendering the failed subtree. Registered
// before the decoding filter below, which rewrites #423/#425 into messages this pattern does not match.
apm.addFilter((payload) => {
	if (!serverHtml) {
		return payload;
	}
	const hydrationError = payload.errors.find((error) => HYDRATION_ERROR.test(error.exception?.message ?? ""));
	if (!hydrationError) {
		return payload;
	}
	try {
		const clientHtml = normalizeHtml(serializeBody());
		const diff = firstDiff(withoutForeignBodyChildren(normalizeHtml(serverHtml), clientHtml), clientHtml);
		hydrationError.context = {
			...hydrationError.context,
			// Labels are searchable, so these are what to filter on. The server renders in UTC; tz and lang
			// expose date and number formatting differences, and the extension flags mismatches we cannot fix.
			tags: {
				...hydrationError.context?.tags,
				...pageStateTags(),
				tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
				hydration_snapshot_while_streaming: serverHtmlStillStreaming,
				...(diff && { hydration_diff_index: diff.index }),
			},
			// Custom context, because APM Server caps label values at 1024 characters.
			...(diff && {
				custom: {
					...hydrationError.context?.custom,
					hydration_diff_path: diff.path,
					hydration_diff_server: diff.server,
					hydration_diff_client: diff.client,
				},
			}),
		};
	} catch {
		// Best effort: the error is still sent as it was captured.
	}
	serverHtml = undefined;
	return payload;
});

// Adds labels to the captured error rather than sending a second event, so APM can split translated pages
// from DOM conflicts in our own code. An error the listener above did not see falls back to the current state.
apm.addFilter((payload) => {
	payload.errors
		.filter((error) => DOM_MUTATION_ERROR.test(error.exception?.message ?? ""))
		.forEach((error) => {
			error.context = {
				...error.context,
				tags: { ...error.context?.tags, ...(domMutationPageStates.shift() ?? pageStateTags()) },
			};
		});
	return payload;
});

// React's production build replaces error messages with a numeric code
// ("Minified React error #418; visit ..."); decode it back to the full
// message before it's sent, so APM shows readable errors instead of codes.
// react-error-decoder only recognizes an optional "Error: " prefix, not the
// "Uncaught Error: " the browser prepends to an uncaught exception's
// message, so that prefix is stripped before decoding.
apm.addFilter((payload) => {
	payload.errors.forEach((error) => {
		if (error.exception && /Minified React error/.test(error.exception.message)) {
			error.exception.message = decode(error.exception.message.replace(/^Uncaught /, ""));
		}
	});
	return payload;
});

// Meta's iOS in-app browsers (Instagram, Facebook) call `_AutofillCallbackHandler` and
// `EmptyRanges` globals from injected script on pages that never defined them. The errors come
// from the webview, not from our code, and do not affect the page, so they are dropped.
// Sentry's default ignore list filters `_AutofillCallbackHandler` for the same reason.
apm.addFilter((payload) => {
	payload.errors = payload.errors.filter((error) => !/Can't find variable: (_AutofillCallbackHandler|EmptyRanges)/.test(error.exception?.message ?? ""));
	return payload;
});

// A tab opened before a deploy asks for chunks the current release no longer serves. One reload fetches
// the current release; the timestamp stops a reload loop when the chunk is missing for another reason.
const CHUNK_RELOAD_KEY = "chunk-load-error-reload";
const CHUNK_RELOAD_MIN_INTERVAL_MS = 60_000;
window.addEventListener("unhandledrejection", (event) => {
	const isChunkLoadError = event.reason instanceof Error && event.reason.name === "ChunkLoadError";
	if (!isChunkLoadError) {
		return;
	}
	try {
		const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY));
		const hasReloadedRecently = Date.now() - lastReload < CHUNK_RELOAD_MIN_INTERVAL_MS;
		if (hasReloadedRecently) {
			return;
		}
		sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
		location.reload();
	} catch {
		// Storage can be blocked; without the guard a reload could loop, so leave the error as reported.
	}
});

// A rejected fetch only says "Failed to fetch" / "Load failed", whoever made it and wherever it went.
// Appending the host says which request failed. Installed after initApm so it wraps the agent's patched
// fetch, and before any third-party script loads, so those scripts pick it up too.
const fetchWithApm = window.fetch;
window.fetch = async (input, init) => {
	try {
		return await fetchWithApm.call(window, input, init);
	} catch (error) {
		if (error instanceof TypeError) {
			try {
				const url = input instanceof Request ? input.url : String(input);
				error.message = `${error.message} (${new URL(url, location.href).host})`;
			} catch {
				// Leave the message as it was rather than lose the error.
			}
		}
		throw error;
	}
};

// Google's ad and analytics tags fire requests whose rejections their own code never handles, so every
// visitor or bot that blocks those hosts logs an unhandled rejection. Nothing on our side can change
// whether a client lets these requests through.
const UNHANDLED_GOOGLE_TAG_FETCH_FAILURE =
	/^Unhandled promise rejection: TypeError: (Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.) \((analytics\.google\.com|www\.google\.com|pagead2\.googlesyndication\.com)\)$/;
apm.addFilter((payload) => {
	payload.errors = payload.errors.filter((error) => !UNHANDLED_GOOGLE_TAG_FETCH_FAILURE.test(error.exception?.message ?? ""));
	return payload;
});

// "Script error." is all the browser reports for an error thrown by a cross-origin script (ads, tag
// manager, consent) or by code a webview host injects, such as Meta's in-app browsers. The message,
// file and stack are withheld from the page, so no instrumentation of ours can recover them. Our own
// bundle is same-origin and always reports its errors in full, so none of these are ours.
apm.addFilter((payload) => {
	payload.errors = payload.errors.filter((error) => !/^Script error\.?$/.test(error.exception?.message ?? ""));
	return payload;
});
